import { ROUTES } from 'constants/routes';
import { ZOHO_TICKET_ID_PATTERN } from 'constants/support-panel';
import { SupportPanelMessageType } from 'enums/support-panel';
import { SupportPanelTicketMessage } from 'interfaces/support-panel.interface';

const isTicketMessage = (data: unknown): data is SupportPanelTicketMessage => {
  if (data === null || typeof data !== 'object') {
    return false;
  }
  const { type, ticketId } = data as Record<string, unknown>;
  return (
    type === SupportPanelMessageType.TICKET &&
    typeof ticketId === 'string' &&
    ZOHO_TICKET_ID_PATTERN.test(ticketId)
  );
};

interface ReadTicketIdParams {
  event: MessageEvent;
  parentOrigins: string[];
  parentWindow: Window;
}

/**
 * Returns the ticket id only when the message comes from an allowed origin, from the window
 * that framed the panel, and has the agreed shape. Anything else is ignored.
 */
export const readTicketIdFromMessage = ({
  event,
  parentOrigins,
  parentWindow
}: ReadTicketIdParams): string | null => {
  if (!parentOrigins.includes(event.origin) || event.source !== parentWindow) {
    return null;
  }
  return isTicketMessage(event.data) ? event.data.ticketId : null;
};

const originOf = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/**
 * The allowed origins to announce readiness to. Narrowed to the parent that framed the panel
 * when the browser says which one it is (`ancestorOrigins` in Chrome, else the referrer, which
 * points elsewhere after a sign-in redirect). Otherwise every allowed origin: `postMessage`
 * delivers only to the one that matches the parent.
 */
export const getReadyTargetOrigins = (parentOrigins: string[]): string[] => {
  const framingOrigin =
    window.location.ancestorOrigins?.[0] ?? originOf(document.referrer);
  return framingOrigin && parentOrigins.includes(framingOrigin)
    ? [framingOrigin]
    : parentOrigins;
};

/**
 * The support panel is framed inside Zoho Desk, where only its own path may be framed, so code that
 * would normally navigate elsewhere (sign-in, /forbidden, /login) must keep the panel on this path.
 */
export const isSupportPanelPage = (): boolean =>
  window.location.pathname === ROUTES.SUPPORT_PANEL;
